import React, { useEffect, useMemo, useRef, useState } from 'react';
import tw, { styled } from 'twin.macro';
import {
  ArrowCounterClockwise,
  CircleNotch,
  Crop,
  Image,
  Sparkle,
} from '@phosphor-icons/react';
import GenerateVideoModal from 'components/modals/GenerateVideoModal';
import { SHARPENING_BRING_TO_LIFE_MESSAGE } from 'utils/sharpening';
import Button from 'components/button/Button';
import { acceptsFieldEdit } from 'utils/templateFieldHelpers';
import { LogoPlaceButtons } from 'components/logo-place/LogoPlaceButtons';
import { BtlPreviewRequest, BtlWordKey } from 'api/video';
import { LogoPlace } from 'types/adEditorTypes';
import { useBtlPreview } from 'hooks/useBtlPreview';
import { useLoopingClock } from 'hooks/useLoopingClock';
import TemplateStylePicker, { LookFrame } from './TemplateStylePicker';
import { StalePreviewNote } from './StalePreviewNote';
import { TEMPLATE_STYLES } from './mockData';
import { planForStyle, StyleExamplesState } from './styleExamples';
import { VideoTemplateStyle } from './types';
import {
  allWordsEmpty,
  anyWordTooLong,
  previewTexts,
  wordTooLong,
  BTL_WORD_FIELDS,
  BtlChoices,
  BtlSuggestions,
  BtlWordEdits,
  BtlWordField,
  btlSummary,
  DEFAULT_BTL_LOGO,
  DEFAULT_BTL_LOOK,
  shownWord,
  textsToSend,
  wordState,
} from './btlWords';

const GENERATING_MESSAGES = [
  'Analysing your image…',
  'Preparing the animation…',
  'Running the AI model…',
  'Almost there…',
  'Wrapping up the final details…',
  'Hang tight — this can take a moment…',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * 'picture-too-small' is its own stage, not a line of red text on the input
 * screen. SYM-6073: the customer cannot fix this by editing what they typed --
 * they have to go and change the crop on the ad -- so the screen that says so
 * is the screen that takes them there.
 */
type ModalStage =
  | 'input'
  | 'generating'
  | 'still-working'
  | 'done'
  | 'error'
  | 'picture-too-small';

interface BringToLifeModalProps {
  show: boolean;
  progress: number; // 0–100
  stage: ModalStage;
  submitError?: string | null;
  /** Why the picture was refused, in words written for a customer. */
  refusalReason?: string | null;
  /**
   * Seeds the screen on open. What they typed and chose survives the trip to
   * the crop tool: the instructions were never the problem, and retyping them
   * is the small insult that made the old message so irritating.
   */
  initialChoices?: Partial<BtlChoices> | null;
  /**
   * SYM-6353. The ad and picture the preview is drawn from: everything the
   * preview request carries except the words and the logo, which this screen
   * adds. Null while there is nothing to preview.
   */
  previewSource?: Omit<BtlPreviewRequest, 'texts' | 'logo_position'> | null;
  /**
   * SYM-6353. The server's refusal of a word (a 422 on `texts.<word>`), shown
   * under that word when the customer is put back on this screen.
   */
  wordErrors?: Partial<Record<BtlWordKey, string>>;
  onSubmit: (choices: BtlChoices) => void;
  onRetry: (choices: BtlChoices) => void;
  /**
   * SYM-6096. Whether widening the crop can fix the refusal. When it cannot
   * (the picture itself is too small, or too small for this placement), the
   * way out is a different picture, and the crop tool is not offered.
   */
  cropCanFixIt?: boolean;
  /** SYM-6135. The server is sharpening a too-small picture before animating it. */
  sharpeningFirst?: boolean;
  /** Close and put the crop tool in front of them. */
  onAdjustCrop: (choices: BtlChoices) => void;
  /** Close and open the picture chooser. */
  onChangePicture: (choices: BtlChoices) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Body = tw.div`flex flex-col items-center gap-5 p-6 w-full max-w-md mx-auto`;

const Title = tw.h2`text-lg font-semibold text-gray-800 text-center`;
const Subtitle = tw.p`text-sm text-gray-500 text-center`;

const Textarea = styled.textarea`
  ${tw`w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
`;

const ProgressBar = tw.div`w-full bg-gray-100 rounded-full h-2 overflow-hidden`;
const ProgressFill = styled.div<{ pct: number }>`
  ${tw`h-2 bg-indigo-500 rounded-full transition-all duration-300`}
  width: ${({ pct }) => pct}%;
`;

// ── SYM-6353: the input screen, mockup v5 ────────────────────────────────
// Two columns, 880px wide (the width of the Track 1 video pop-up): the chosen
// look large on the left, the choices on the right. Below 760px the columns
// stack and the pop-up scrolls. The footer with the button is pinned to the
// bottom of the pop-up, so on a short window Generate stays in reach.

const InputScreen = tw.div`flex flex-col gap-4 w-full text-left pt-2`;

const Head = tw.div`flex items-center gap-2.5 pr-10`;
const HeadIcon = tw.div`grid place-items-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-500`;
const HeadTitle = tw.h2`text-lg font-semibold text-gray-800`;

const Columns = styled.div`
  ${tw`grid gap-5 items-start`}
  grid-template-columns: 260px minmax(0, 1fr);

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const LeftColumn = styled.div`
  @media (max-width: 760px) {
    max-width: 220px;
  }
`;

const LookNote = tw.p`mt-2 text-sm text-gray-500`;

const RightColumn = tw.div`grid gap-3.5 min-w-0`;

const SectionLabel = tw.label`block text-sm font-semibold text-gray-800 mb-1`;

const MoveBox = styled.textarea`
  ${tw`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
`;

const WordRow = styled.div`
  ${tw`grid items-center`}
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 2px 10px;
`;

const WordLabel = tw.label`text-sm font-semibold text-gray-800`;

const WordBox = tw.div`flex items-center gap-1.5 border border-gray-200 rounded-lg px-2.5 bg-white focus-within:(ring-2 ring-indigo-500 border-transparent)`;

const WordInput = tw.input`flex-1 min-w-0 border-0 bg-transparent py-1.5 text-sm text-gray-800 focus:outline-none`;

const WordCounter = styled.span<{ $over: boolean }>`
  ${tw`text-xs tabular-nums`}
  ${({ $over }) =>
    $over ? tw`text-amber-600 font-semibold` : tw`text-gray-400`}
`;

const TooLong = tw.p`col-start-2 text-xs font-medium text-amber-700`;

const WordMeta = tw.div`col-start-2 flex flex-wrap items-center gap-2.5 min-h-[16px] text-xs text-gray-500`;

const AutoChip = tw.span`rounded px-1.5 text-xs font-semibold bg-green-50 text-green-700`;

const EditChip = tw.span`rounded px-1.5 text-[10.5px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700`;

const ResetLink = tw.button`border-0 bg-transparent p-0 text-xs text-indigo-600 underline cursor-pointer`;

const WordError = tw.p`col-start-2 text-xs font-medium text-red-700`;

const WordsNotLoaded = tw.p`mt-2 text-sm text-amber-700`;

const NoWords = tw.p`mt-2 rounded-lg bg-red-50 px-2.5 py-2 text-sm font-medium text-red-700`;

const Footer = tw.div`sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white pt-3.5 pb-2`;

const Summary = tw.small`text-sm text-gray-500`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * One of the three words. SYM-6353.
 *
 * Filled in with what the image ad shows and editable. Under the box: the
 * green "Automatic (from ...)" while it is the suggestion, or "Custom" /
 * "Hidden" and a link that puts the suggestion back, naming it.
 */
const WordField: React.FC<{
  field: BtlWordField;
  edits: BtlWordEdits;
  suggestions: BtlSuggestions;
  disabled: boolean;
  /** The server's refusal of this word, if it refused it. */
  error?: string;
  onChange: (key: BtlWordKey, value: string | undefined) => void;
}> = ({ field, edits, suggestions, disabled, error, onChange }) => {
  // Before the suggestions are known the box shows nothing and says nothing:
  // a chip saying "Automatic" over an empty box would claim a word that is
  // not there.
  const known = suggestions !== null;
  const value = known ? shownWord(field.key, edits, suggestions) : '';
  const state = wordState(field.key, edits, suggestions);
  const suggestion = suggestions?.[field.key] ?? '';
  const tooLong = wordTooLong(field, edits, suggestions);
  const id = `btl-word-${field.key}-input`;

  return (
    <WordRow data-testid={`btl-word-${field.key}`}>
      <WordLabel htmlFor={id}>{field.label}</WordLabel>
      <WordBox>
        <WordInput
          id={id}
          value={value}
          disabled={disabled}
          // Native cap, so paste and drag-and-drop stop there too; the check
          // below is the real guard, as in the image-ad fields (SYM-5907). A
          // suggestion already longer than the cap is left whole, not cut.
          maxLength={Math.max(field.cap, value.length)}
          onChange={(e) => {
            if (acceptsFieldEdit(e.target.value, value, field.cap)) {
              onChange(field.key, e.target.value);
            }
          }}
        />
        {field.showCounter && (
          <WordCounter
            data-testid={`btl-word-${field.key}-counter`}
            $over={tooLong}
          >
            {value.length}/{field.cap}
          </WordCounter>
        )}
      </WordBox>
      <WordMeta>
        {!known ? null : state === 'automatic' ? (
          <AutoChip>{field.automatic}</AutoChip>
        ) : (
          <>
            <EditChip>{state === 'hidden' ? 'Hidden' : 'Custom'}</EditChip>
            <ResetLink
              type="button"
              onClick={() => onChange(field.key, undefined)}
            >
              {suggestion ? `${field.reset} (${suggestion})` : field.reset}
            </ResetLink>
          </>
        )}
      </WordMeta>
      {tooLong && (
        <TooLong data-testid={`btl-word-${field.key}-too-long`}>
          Shorten to {field.cap} characters to use your own text
        </TooLong>
      )}
      {error && (
        <WordError role="alert" data-testid={`btl-word-${field.key}-error`}>
          {error}
        </WordError>
      )}
    </WordRow>
  );
};

const BringToLifeModal: React.FC<BringToLifeModalProps> = ({
  show,
  progress,
  stage,
  submitError,
  refusalReason,
  cropCanFixIt = true,
  sharpeningFirst = false,
  initialChoices = null,
  previewSource = null,
  wordErrors,
  onSubmit,
  onRetry,
  onAdjustCrop,
  onChangePicture,
  onClose,
}) => {
  const [instructions, setInstructions] = useState(
    initialChoices?.instructions ?? ''
  );
  const [look, setLook] = useState<VideoTemplateStyle>(
    initialChoices?.look ?? DEFAULT_BTL_LOOK
  );
  const [logo, setLogo] = useState<LogoPlace>(
    initialChoices?.logo ?? DEFAULT_BTL_LOGO
  );
  const [edits, setEdits] = useState<BtlWordEdits>(initialChoices?.edits ?? {});
  const [messageIdx, setMessageIdx] = useState(0);
  const prevShowRef = useRef(false);

  // Reset the screen whenever the modal opens fresh -- to whatever the caller
  // seeded it with, which is nothing on a normal open and the previous choices
  // when they are coming back from correcting the crop.
  useEffect(() => {
    if (show && !prevShowRef.current && stage === 'input') {
      setInstructions(initialChoices?.instructions ?? '');
      setLook(initialChoices?.look ?? DEFAULT_BTL_LOOK);
      setLogo(initialChoices?.logo ?? DEFAULT_BTL_LOGO);
      setEdits(initialChoices?.edits ?? {});
    }
    prevShowRef.current = show;
  }, [show, stage, initialChoices]);

  // ── SYM-6353: the preview and the words ───────────────────────────────────
  // The suggestions arrive with the preview. Until the first answer is here
  // they are unknown, and the three boxes wait for them rather than start
  // empty and let the customer type over a word that is about to appear.
  const [suggestions, setSuggestions] = useState<BtlSuggestions>(null);

  // Without the suggestions nothing can be told apart from them, so no word is
  // sent at all and the server uses the ad's own (the contract's absent key).
  const texts = suggestions ? textsToSend(edits, suggestions) : null;
  // The preview never carries a word still over its cap (see previewTexts).
  const shownTexts = suggestions ? previewTexts(edits, suggestions) : null;
  const textsKey = JSON.stringify(shownTexts);
  const previewRequest = useMemo<BtlPreviewRequest | null>(
    () =>
      show && stage === 'input' && previewSource
        ? {
            ...previewSource,
            ...(shownTexts ? { texts: shownTexts } : {}),
            logo_position: logo,
          }
        : null,
    // `shownTexts` is a new object every render; `textsKey` is its content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [show, stage, previewSource, textsKey, logo]
  );
  const {
    state: preview,
    retry: retryPreview,
    asking: previewAsking,
  } = useBtlPreview(previewRequest);
  // A later refresh failed: the preview shown is for an earlier choice.
  const previewIsStale = preview.status === 'ready' && !!preview.stale;

  useEffect(() => {
    if (preview.status === 'ready' && preview.answer?.suggestions) {
      setSuggestions(preview.answer.suggestions);
    }
  }, [preview]);

  const examples: StyleExamplesState = useMemo(
    () =>
      preview.status === 'ready'
        ? {
            status: 'ready',
            examples: preview.answer.examples ?? [],
            brandHasOwnColour: !!preview.answer.brand_has_own_colour,
            brandId: preview.answer.brand_id ?? null,
          }
        : preview,
    [preview]
  );
  const bigPlan =
    examples.status === 'ready' ? planForStyle(examples.examples, look) : null;
  const clock = useLoopingClock(show && stage === 'input' && !!bigPlan);

  // The words can be edited only once the suggestions are known. If they never
  // come, the boxes stay locked and Generate still works: no word is sent, and
  // the server draws the ad's own. Unlocking them empty would let a customer
  // type and delete a word and send "hide the callout" without meaning to.
  const wordsReady = suggestions !== null;
  const wordsNotLoaded = !wordsReady && preview.status === 'failed';
  const noWords = wordsReady && allWordsEmpty(edits, suggestions);
  // Option A: an edited word over its cap holds Generate until it fits.
  const tooLong = anyWordTooLong(edits, suggestions);
  const cannotGenerate = noWords || tooLong;

  // The server's refusal of a word stands until that word is edited.
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<BtlWordKey, string>>
  >(wordErrors ?? {});
  useEffect(() => {
    setFieldErrors(wordErrors ?? {});
  }, [wordErrors]);

  const setWord = (key: BtlWordKey, value: string | undefined) => {
    if (!wordsReady) return;
    setFieldErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    setEdits((current) => {
      const next = { ...current };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const choices = (): BtlChoices => ({
    instructions: instructions.trim(),
    look,
    logo,
    edits,
    texts,
  });

  useEffect(() => {
    if (stage !== 'generating') return;
    setMessageIdx(0);
    const t = setInterval(() => {
      setMessageIdx((i) => Math.min(i + 1, GENERATING_MESSAGES.length - 1));
    }, 18_000);
    return () => clearInterval(t);
  }, [stage]);

  const handleSubmit = () => {
    if (cannotGenerate) return;
    onSubmit(choices());
  };

  const handleRetry = () => {
    onRetry(choices());
  };

  const handleAdjustCrop = () => {
    onAdjustCrop(choices());
  };

  const handleChangePicture = () => {
    onChangePicture(choices());
  };

  // What dismissing the too-small screen does: the one fix that can work.
  const handleTooSmallExit = cropCanFixIt
    ? handleAdjustCrop
    : handleChangePicture;

  const isGenerating = stage === 'generating';

  // Dismissing the too-small screen IS "take me to the crop tool". There is
  // nothing else to do from here, so the X does the same as the button rather
  // than dropping the customer back where they started.
  const isTooSmall = stage === 'picture-too-small';

  return (
    <GenerateVideoModal
      show={show}
      wide={stage === 'input'}
      hideCloseButton={isGenerating}
      onClose={isTooSmall ? handleTooSmallExit : onClose}
    >
      {/* ── Input stage (SYM-6353, mockup v5) ──────────────────────────── */}
      {stage === 'input' && (
        <InputScreen data-testid="btl-input">
          <Head>
            <HeadIcon>
              <Sparkle size={18} weight="fill" />
            </HeadIcon>
            <HeadTitle>Bring your image to life</HeadTitle>
          </Head>

          <Columns data-testid="btl-columns">
            <LeftColumn>
              <div data-testid="btl-big-preview" data-style={look}>
                <LookFrame
                  plan={bigPlan}
                  clock={clock}
                  loading={preview.status === 'loading'}
                  maxHeight="420px"
                />
              </div>
              {previewIsStale && (
                <StalePreviewNote busy={previewAsking} onRetry={retryPreview} />
              )}
              <LookNote>
                {TEMPLATE_STYLES.find((style) => style.id === look)?.onAClip}.
                The picture itself moves in the clip.
              </LookNote>
            </LeftColumn>

            <RightColumn>
              <div>
                <SectionLabel htmlFor="btl-move">
                  How should the picture move?{' '}
                  <span tw="font-normal text-gray-500">(optional)</span>
                </SectionLabel>
                <MoveBox
                  id="btl-move"
                  rows={2}
                  placeholder="e.g. the nurse turns and smiles, the camera slowly pushes in"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  autoFocus
                />
              </div>

              <div>
                <SectionLabel as="span">Words on the video</SectionLabel>
                <div tw="grid gap-1.5">
                  {BTL_WORD_FIELDS.map((field) => (
                    <WordField
                      key={field.key}
                      field={field}
                      edits={edits}
                      suggestions={suggestions}
                      disabled={!wordsReady}
                      error={fieldErrors[field.key]}
                      onChange={setWord}
                    />
                  ))}
                </div>
                {wordsNotLoaded && (
                  <WordsNotLoaded data-testid="btl-words-not-loaded">
                    We couldn&apos;t load the words.{' '}
                    <ResetLink type="button" onClick={retryPreview}>
                      Try again
                    </ResetLink>
                  </WordsNotLoaded>
                )}
                {noWords && (
                  <NoWords role="alert" data-testid="btl-no-words">
                    Add at least one line of text. A video without words has
                    nothing to say about the job.
                  </NoWords>
                )}
              </div>

              <div>
                <SectionLabel as="span">Look</SectionLabel>
                <TemplateStylePicker
                  compact
                  onAClip
                  label={null}
                  selected={look}
                  onSelect={setLook}
                  examples={examples}
                  onRetryExamples={retryPreview}
                />
              </div>

              <div>
                <SectionLabel as="span">Logo</SectionLabel>
                <div tw="flex flex-wrap gap-2" data-testid="btl-logo-row">
                  <LogoPlaceButtons value={logo} onPick={setLogo} />
                </div>
              </div>

              {submitError && <p tw="text-sm text-amber-600">{submitError}</p>}
            </RightColumn>
          </Columns>

          <Footer data-testid="btl-footer">
            <Summary data-testid="btl-summary">
              {btlSummary(look, logo)}
            </Summary>
            <Button
              variant="indigo"
              disabled={cannotGenerate}
              onClick={handleSubmit}
              testId="btl-generate"
            >
              <span tw="inline-flex items-center justify-center">
                <Sparkle size={16} weight="fill" tw="mr-2" />
                Generate video clip
              </span>
            </Button>
          </Footer>
        </InputScreen>
      )}

      {stage !== 'input' && (
        <Body>
          {/* ── Generating stage ──────────────────────────────────────── */}
          {stage === 'generating' && (
            <>
              <CircleNotch
                size={40}
                weight="bold"
                tw="text-indigo-500 animate-spin"
              />
              <Title>{GENERATING_MESSAGES[messageIdx]}</Title>
              {sharpeningFirst && (
                <Subtitle data-testid="btl-sharpening">
                  {SHARPENING_BRING_TO_LIFE_MESSAGE}
                </Subtitle>
              )}
              <Subtitle>
                This usually takes 40-60 seconds. You can close this window and
                carry on; the clip is added to your ad as soon as it is ready.
              </Subtitle>
              <ProgressBar>
                <ProgressFill pct={progress} />
              </ProgressBar>
              <p tw="text-xs text-gray-400">{Math.round(progress)}%</p>
              <Button variant="outline" size="small" onClick={onClose}>
                Close and wait in background
              </Button>
            </>
          )}

          {/* ── Still working stage ───────────────────────────────────── */}
          {/* The browser stopped waiting; the generation did not stop. It runs
            server-side and attaches itself to the ad when it finishes, so this
            offers no retry: retrying would only hit the lock the live
            generation still holds. See SYM-5858. */}
          {stage === 'still-working' && (
            <>
              <CircleNotch
                size={40}
                weight="bold"
                tw="text-indigo-500 animate-spin"
              />
              <Title>Still being made</Title>
              <Subtitle>
                This one is taking longer than usual, but it has not failed and
                nothing is lost. You can close this window and carry on; the
                clip is added to your ad as soon as it is ready.
              </Subtitle>
              <Button variant="indigo" stretch onClick={onClose}>
                Close and carry on
              </Button>
            </>
          )}

          {/* ── Picture too small ─────────────────────────────────────── */}
          {stage === 'picture-too-small' && (
            <>
              <Crop size={40} weight="bold" tw="text-amber-500" />
              <Title>This picture is too small to animate</Title>
              <Subtitle>{refusalReason}</Subtitle>
              {cropCanFixIt ? (
                <Button variant="indigo" stretch onClick={handleAdjustCrop}>
                  <span tw="inline-flex items-center justify-center">
                    <Crop size={16} weight="bold" tw="mr-2" />
                    Adjust the crop
                  </span>
                </Button>
              ) : (
                // SYM-6096. No crop of this picture can work, so the crop tool is
                // not offered. Once SYM-6135 ships, the server sharpens such a
                // picture through fal before refusing, so this screen is only
                // reached when even that could not rescue it.
                <Button variant="indigo" stretch onClick={handleChangePicture}>
                  <span tw="inline-flex items-center justify-center">
                    <Image size={16} weight="bold" tw="mr-2" />
                    Change the picture
                  </span>
                </Button>
              )}
            </>
          )}

          {/* ── Error stage ───────────────────────────────────────────── */}
          {stage === 'error' && (
            <>
              <Sparkle size={40} weight="fill" tw="text-gray-300" />
              <Title>Something went wrong</Title>
              <Subtitle>
                The video clip couldn't be generated. Try adjusting your
                instructions and try again.
              </Subtitle>
              <Textarea
                rows={4}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
              />
              <div tw="flex gap-3 w-full">
                <Button variant="outline" stretch onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="indigo" stretch onClick={handleRetry}>
                  <span tw="inline-flex items-center justify-center">
                    <ArrowCounterClockwise size={16} tw="mr-2" />
                    Try again
                  </span>
                </Button>
              </div>
            </>
          )}
        </Body>
      )}
    </GenerateVideoModal>
  );
};

export default BringToLifeModal;
